class VTSProductTabs {
    constructor(el) {
        this.content = el.querySelector('.product-tabs__content');
        this.pin = el.querySelector('.product-tabs__pin');
        this.sections = el.querySelectorAll('.product-tabs__section');
        this.buttons = el.querySelectorAll('.product-tabs__section-button');
        this.sectionsCount = this.sections.length;
        this.activeIndex = 0;
        this.isClickScrolling = false;
        this.prefersReducedMotion = prefersReducedMotion();
        // Matches the md breakpoint, where the videos sit beside the list instead of in it.
        this.mdQuery = window.matchMedia('(min-width: 767px)');
        // Matches the lg breakpoint; below it the sections don't stick and scrolling doesn't
        // change the active section.
        this.lgQuery = window.matchMedia('(min-width: 1024px)');

        gsap.registerPlugin(ScrollTrigger);

        this.toggleScrollTrigger();
        // The trigger sets the initial state itself once it's measured.
        if (!this.scrollTrigger) {
            this.setActive(0, true);
        }
        this.bindEvents();
    }

    getStickyTop() {
        return parseFloat(getComputedStyle(this.pin).top);
    }

    toggleScrollTrigger() {
        if (this.lgQuery.matches) {
            this.animate();
        } else if (this.scrollTrigger) {
            this.scrollTrigger.kill();
            this.scrollTrigger = null;
        }
    }

    // Spans exactly the stretch where the sections are stuck, split evenly between them;
    // the pin is sticky, so it can't be a reliable trigger itself.
    animate() {
        this.initialized = false;
        this.scrollTrigger = ScrollTrigger.create({
            trigger: this.content,
            start: () => `top ${this.getStickyTop()}px`,
            end: () =>
                `bottom ${this.getStickyTop() + this.pin.offsetHeight}px`,
            onRefresh: (self) => {
                if (!this.initialized) {
                    this.initialized = true;
                    this.setActive(this.getIndex(self.progress), true);
                }
            },
            onUpdate: (self) => {
                if (this.initialized && !this.isClickScrolling) {
                    this.setActive(this.getIndex(self.progress));
                }
            }
        });
    }

    getIndex(progress) {
        return Math.min(
            this.sectionsCount - 1,
            Math.floor(progress * this.sectionsCount)
        );
    }

    setActive(index, immediate = false) {
        if (index === this.activeIndex && !immediate) {
            return;
        }

        const animate = !immediate && !this.prefersReducedMotion;

        this.activeIndex = index;
        this.sections.forEach((section, i) => {
            const isActive = i === index;
            const body = section.querySelector('.product-tabs__section-body');
            const video = section.querySelector('.product-tabs__video');
            const bodySettings = {
                height: isActive ? 'auto' : 0,
                overwrite: true
            };
            const videoSettings = {
                autoAlpha: isActive ? 1 : 0,
                overwrite: true
            };

            // Stacked videos on mobile open and close with their section; on desktop they
            // share one spot and only fade.
            if (this.mdQuery.matches) {
                gsap.killTweensOf(video, 'height');
                gsap.set(video, { clearProps: 'height' });
            } else {
                videoSettings.height = isActive ? 'auto' : 0;
            }

            section.classList.toggle('is-active', isActive);
            section
                .querySelector('.product-tabs__section-button')
                .setAttribute('aria-expanded', isActive);
            body.inert = !isActive;

            if (animate) {
                gsap.to(body, bodySettings);
                gsap.to(video, videoSettings);
            } else {
                gsap.set(body, bodySettings);
                gsap.set(video, videoSettings);
            }

            this.toggleVideo(video, isActive);
        });
    }

    // Plays only the visible video; the rest pause so they don't run hidden. Which of a
    // section's players is displayed is left to the CSS.
    toggleVideo(video, isActive) {
        video.querySelectorAll('.product-tabs__player').forEach((wrapper) => {
            const player = VTSWistiaPlayer.instances.get(
                wrapper.querySelector('wistia-player')?.id
            );

            if (!player) {
                return;
            }

            if (!isActive || !wrapper.offsetParent) {
                player.pause();
            } else if (!this.prefersReducedMotion) {
                player.play();
            }
        });
    }

    getScrollPosition(index) {
        const { start, end } = this.scrollTrigger;
        const position =
            start +
            ((end - start) * index) / this.sectionsCount +
            (index > 0 ? 1 : 0);

        return Math.min(position, ScrollTrigger.maxScroll(window));
    }

    // Activates the clicked section right away, then scrolls to where the trigger would have
    // activated it, ignoring scroll updates on the way so the sections in between don't flash.
    goTo(index) {
        if (!this.scrollTrigger) {
            this.setActive(index);
            return;
        }

        const top = this.getScrollPosition(index);

        this.setActive(index);

        if (Math.abs(window.scrollY - top) < 1) {
            return;
        }

        this.isClickScrolling = true;
        this.onScrollSettled(() => {
            this.isClickScrolling = false;
            if (!this.scrollTrigger) {
                return;
            }
            // Resync in case the user scrolled again and interrupted the smooth scroll.
            this.setActive(this.getIndex(this.scrollTrigger.progress));
        });
        window.scrollTo({
            top,
            left: 0,
            behavior: this.prefersReducedMotion ? 'instant' : 'smooth'
        });
    }

    // Debounced scroll rather than `scrollend`, which older Safari doesn't fire.
    onScrollSettled(callback) {
        let timeout;
        const onScroll = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                window.removeEventListener('scroll', onScroll);
                callback();
            }, 150);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
    }

    bindEvents() {
        this.buttons.forEach((button, i) => {
            button.addEventListener('click', () => this.goTo(i));
        });
        this.lgQuery.addEventListener('change', () =>
            this.toggleScrollTrigger()
        );
        // Re-apply the active state so heights left over from the other layout don't stick.
        this.mdQuery.addEventListener('change', () =>
            this.setActive(this.activeIndex, true)
        );
    }
}

document
    .querySelectorAll('.product-tabs')
    .forEach((el) => new VTSProductTabs(el));
